// domain/services/authorizationStatus.js
import { AUTHORIZATION_STATUS } from '../constants/authorization';
import { coversService, admitsSetting } from '../model/ApprovedLimit';
import { includes, startsAfter } from '../model/EffectivePeriod';

/**
 * Where an authorization sits relative to a date, and whether it answers the presence question.
 *
 * `asOf` IS AN ARGUMENT, NEVER A CLOCK READ. Exactly one layer reads the clock — the composable
 * deciding what "now" means for a screen. A rule fetching its own as-of would be untestable without
 * freezing time, and two clock reads in one session can disagree across midnight.
 */

/**
 * Upcoming, active or ended, as of a date.
 *
 * @param {import('../model/Authorization.types').Authorization} authorization
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {import('../constants/authorization').AuthorizationStatus}
 */
export function authorizationStatus(authorization, asOf) {
  if (startsAfter(authorization.period, asOf)) {
    return AUTHORIZATION_STATUS.UPCOMING;
  }

  return includes(authorization.period, asOf)
    ? AUTHORIZATION_STATUS.ACTIVE
    : AUTHORIZATION_STATUS.ENDED;
} // authorizationStatus()

/**
 * Is the approval recorded but its number not yet issued?
 *
 * A WORKLIST FACT, NEVER A CLAIM GATE. Some funders never issue a number, so holding on its absence
 * would produce a hold nobody could ever clear.
 *
 * @param {import('../model/Authorization.types').Authorization} authorization
 * @returns {boolean}
 */
export function isNumberPending(authorization) {
  return authorization.authorizationNumber === null;
} // isNumberPending()

/**
 * Does this authorization approve the given service, in the given setting, on the given date?
 *
 * It answers on SCOPE ALONE. Nothing here counts anything — how much of a limit remains is
 * consumption, deferred until a claim line can substantiate units.
 *
 * THE CALLER MUST GATE ON THE REQUIREMENT FIRST, and getting that order wrong would make the whole
 * bet unmeasurable. `billWithoutAuthorization` is THREE-VALUED: `true` waives the requirement,
 * `false` requires one, `null` means the billing channel carries no prior authorization at all.
 * Asking about a payer that needs none produces a hold for every activity under it, flooding the
 * histogram with noise. The projector's `authorizationResolver` gates on
 * `carriesPriorAuthorization(billingChannel) && billWithoutAuthorization === false`; this takes the
 * same precondition.
 *
 * A tombstoned authorization never answers yes. Readers filter `isDeleted` at the query and this
 * checks again: a redundant check costs nothing, a tombstone clearing a hold costs a claim for work
 * no funder approved.
 *
 * @param {import('../model/Authorization.types').Authorization} authorization
 * @param {{ treatmentServiceId: string, placeOfServiceCode: string|null, asOf: string }} activity
 * @returns {boolean}
 */
export function approves(authorization, { treatmentServiceId, placeOfServiceCode, asOf }) {
  if (authorization.isDeleted === true || !includes(authorization.period, asOf)) {
    return false;
  }

  return authorization.limits.some((limit) => {
    return coversService(limit, treatmentServiceId) && admitsSetting(limit, placeOfServiceCode);
  });
} // approves()

/**
 * Is a required authorization present among these, for this service, setting and date?
 *
 * The list form of `approves`, and the shape the gate calls. Same precondition.
 *
 * @param {import('../model/Authorization.types').Authorization[]} authorizations - the client's,
 *   already narrowed to one funding source by the caller
 * @param {{ treatmentServiceId: string, placeOfServiceCode: string|null, asOf: string }} activity
 * @returns {boolean}
 */
export function hasPresentAuthorization(authorizations, activity) {
  return (authorizations ?? []).some((authorization) => approves(authorization, activity));
} // hasPresentAuthorization()
